import React, { useEffect, useRef, useState } from 'react';
import { candidateAPI } from '../services/api';

const NotificationBell = ({ onNavigate, refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const response = await candidateAPI.getNotifications();
      setItems(response.data?.notifications || []);
      setUnreadCount(response.data?.unreadCount || 0);
    } catch (error) {
      setItems([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const markRead = async (ids) => {
    if (!ids.length) return;
    try {
      await candidateAPI.updatePreferences({ readNotificationIds: ids });
      setItems((prev) => prev.map((item) => (ids.includes(item.id) ? { ...item, read: true } : item)));
      setUnreadCount((prev) => Math.max(0, prev - ids.length));
    } catch (error) {
      // Keep the list usable even if persistence fails.
    }
  };

  const handleClick = async (item) => {
    if (!item.read) {
      await markRead([item.id]);
    }
    setOpen(false);
    if (onNavigate) onNavigate(item);
  };

  const unreadIds = items.filter((item) => !item.read).map((item) => item.id);
  const hasUnread = unreadCount > 0;

  return (
    <div className="group relative" data-tour="notifications" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((value) => {
            const next = !value;
            if (!value) load();
            return next;
          });
        }}
        className="btn-ghost relative"
        aria-label={hasUnread ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg
          className="w-5 h-5"
          fill={hasUnread ? 'currentColor' : 'none'}
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-4 px-1 rounded-full bg-primary-600 text-white text-[10px] leading-4 text-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {!open && <span className="header-icon-flyout">Notifications</span>}
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
          role="dialog"
          aria-label="Notifications"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => markRead(unreadIds)}
                >
                  Mark as read
                </button>
              )}
              <button
                type="button"
                className="btn-ghost"
                aria-label="Close notifications"
                onClick={() => setOpen(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No notifications yet.</p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleClick(item)}
                  className="btn-ghost w-full justify-start"
                >
                  <span className="flex min-w-0 flex-col items-start text-left">
                    {!item.read && <span className="sr-only">Unread</span>}
                    <span className="text-xs uppercase tracking-wide text-gray-400">{item.category}</span>
                    <span className={`text-sm mt-0.5 ${item.read ? 'font-medium text-gray-900' : 'font-semibold text-secondary-900'}`}>
                      {item.title}
                    </span>
                    <span className="text-xs text-gray-600 mt-1">{item.body}</span>
                    {item.timestamp && (
                      <span className="text-[11px] text-gray-400 mt-1">{new Date(item.timestamp).toLocaleString()}</span>
                    )}
                    <span className="text-xs text-primary-600 mt-2">{item.cta}</span>
                  </span>
                </button>
              ))
            )}
          </div>
          {items.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => markRead(unreadIds)}
              >
                View all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
